import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { components } from "react-select";
import { FormLabel, FormSelect } from "../../../components/forms";
import NewPagedSelect from "../../../components/forms/NewPagedSelect";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";
import { patchReplace } from "../../../helpers/patchDoc";
import useFundDetails from "../hooks/useFundDetails";
import FundSedolSearch from "./FundSedolSearch";

const AssetDetails = ({ setAssetNameDisplayText, setMonitoringDisplayText }) => {
    const [{ patchAssetGroup, patchAssetGroupSingle, fetchSectors, searchSectors, fetchSector, makeAssetPassive }, { assetGroup, assetGroupIsNotReady }, { canEditDetails, options }] = useFundDetails();

    const [realTimeMonitoring, setRealTimeMonitoring] = useState(assetGroup?.monitoring);
    const shouldMakeAssetPassive = useRef(null);

    useEffect(() => {
        setRealTimeMonitoring(assetGroup?.monitoring);
    }, [assetGroup?.monitoring])

    const showMonitoringWarning = () => {
        openConfirmModal({
            confirmButtonText: "I'm sure!",
            cancelButtonText: "Cancel",
            confirmButtonVariant: "success",
            message: <>
                You are about to change the Monitoring of this asset to 'Passive', which will remove it from all Core Funds management.
                <br />
                <strong>This action cannot be undone.</strong>
            </>
        }).then(
            (proceed) => {
                if (proceed)
                    shouldMakeAssetPassive.current = true;
                else {
                    shouldMakeAssetPassive.current = false;
                    setRealTimeMonitoring(assetGroup?.monitoring);
                    setMonitoringDisplayText(options.monitoring.find(opt => opt.value === assetGroup?.monitoring)?.label)
                }
            }
        );
    }

    return <>
        <div className="row">
            {/* Sedol Search is kept in its own component for readability */}
            <FundSedolSearch setAssetNameDisplayText={setAssetNameDisplayText} disabled={!canEditDetails} />

            <div className="col-6 col-md-4 col-xl-3 flex-column mt-2">
                <FormLabel>
                    Sector/Benchmark
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <NewPagedSelect
                        defaultValue={assetGroup?.sectorCodeOverride}
                        pageQueryFn={fetchSectors}
                        limit={10}
                        searchOptionsFn={searchSectors}
                        fetchValueFn={fetchSector}
                        onBlur={(selection) => patchAssetGroup([
                            patchReplace("sectorCodeOverride", selection.value),
                            patchReplace("sectorCodeIsOverridden", assetGroup?.assetSectorCode !== selection.value)
                        ])}
                        isDisabled={!canEditDetails}
                        components={{
                            ...components,
                            Option: (props) => <components.Option className="lh-sm" {...props}>
                                <div>{props.data.label}</div>
                                <span className={classNames("small", { "text-muted": !props.isSelected })}>{props.data.value}</span>
                            </components.Option>
                        }}
                    />}
            </div>
            <div className="col-6 col-md-5 col-lg-4 col-xl-3 flex-column mt-2">
                <FormLabel>
                    Monitoring
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <FormSelect
                        options={options.monitoring}
                        defaultValue={realTimeMonitoring}
                        onChange={(selection) => {
                            setMonitoringDisplayText(selection.label);
                        }}
                        onBlur={(selection) => {
                            return new Promise((resolve, reject) => {
                                if (selection.value !== 2)
                                    return patchAssetGroupSingle("monitoring", selection.value).then(resolve, reject);

                                showMonitoringWarning();
                                setRealTimeMonitoring(selection.value)

                                const modalCloseInterval = setInterval(() => {
                                    if (shouldMakeAssetPassive.current != null) {
                                        clearInterval(modalCloseInterval);

                                        if (shouldMakeAssetPassive.current === true)
                                            return makeAssetPassive().then(resolve, reject);
                                        else
                                            return reject();
                                    }
                                }, 100);
                            }).finally(() => {
                                shouldMakeAssetPassive.current = null;
                            });
                        }}
                        isDisabled={!canEditDetails}
                    />}
            </div>

            <div className="col-4 col-md-3 col-xl-2 flex-column mt-2">
                <FormLabel>
                    Management
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <FormSelect
                        options={options.management}
                        defaultValue={assetGroup?.management}
                        onBlur={(selection) => patchAssetGroupSingle("management", selection.value)}
                        isDisabled={!canEditDetails}
                    />}
            </div>
            <div className="col-8 col-md-5 col-xl-4 flex-column mt-2">
                <FormLabel>
                    Asset Class
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <FormSelect
                        options={options.assetClass}
                        defaultValue={assetGroup?.assetClass}
                        onBlur={(selection) => patchAssetGroupSingle("assetClass", selection.value)}
                        isDisabled={!canEditDetails}
                    />}
            </div>
            <div className="col-6 col-md-4 flex-column mt-2">
                <FormLabel>
                    Region
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <FormSelect
                        options={options.region}
                        defaultValue={assetGroup?.region}
                        onBlur={(selection) => patchAssetGroupSingle("region", selection.value)}
                        isDisabled={!canEditDetails}
                    />}
            </div>
            <div className="col-3 col-xl-2 flex-column mt-2">
                <FormLabel>
                    Style
                </FormLabel>
                {assetGroupIsNotReady
                    ? <Skeleton />
                    : <FormSelect
                        options={options.style}
                        defaultValue={assetGroup?.style}
                        onBlur={(selection) => patchAssetGroupSingle("style", selection.value)}
                        isDisabled={!canEditDetails}
                    />}
            </div>
        </div>
    </>;
}

export default AssetDetails;